/** Exposes debug commands on window.dbg for browser console access. */
import Phaser from 'phaser';
import { DebugConfig, DebugFlagKey } from './DebugConfig';
import { ConfigLoader } from '../systems/ConfigLoader';
import { randomAngle, angleToDirection, randomInRange } from '../utils/Math';

/** Declare global augmentation for window.dbg. */
declare global {
  interface Window {
    dbg: DebugConsoleAPI;
  }
}

interface DebugConsoleAPI {
  /** List all available commands. */
  help(): void;

  // --- Flag toggles ---
  /** Toggle a debug flag by name (e.g. 'playerGodMode'). */
  flag(name: string): void;
  /** Show all flag states. */
  flags(): void;
  /** Enable/disable the debug master toggle. */
  master(on?: boolean): void;

  // --- Player ---
  /** Set player HP to a value. */
  hp(value: number): void;
  /** Heal the player to full. */
  heal(): void;
  /** Kill the player. */
  kill(): void;

  // --- Enemies ---
  /** Spawn N enemies of a given archetype near the player. */
  spawn(archetype: string, count?: number): void;
  /** Despawn all enemies. */
  despawnAll(): void;

  // --- XP ---
  /** Grant XP to a skill tree (or 'all'). */
  xp(tree: string, amount: number): void;

  // --- Slots ---
  /** Set attunement slots to max. */
  maxSlots(): void;
}

/** Get the GameScene from the Phaser game instance. */
function getGameScene(): any {
  const game = (window as any).phaserGame as Phaser.Game | undefined;
  if (!game) { console.warn('dbg: No Phaser game found'); return null; }
  return game.scene.getScene('GameScene');
}

function log(msg: string): void {
  console.log(`%c[dbg] ${msg}`, 'color: #44ff44');
}

/** Install the debug console API on window.dbg. Call once after game init. */
export function installDebugConsole(game: Phaser.Game): void {
  // Store game reference for getGameScene
  (window as any).phaserGame = game;

  const api: DebugConsoleAPI = {
    help() {
      console.log(`%c
=== Debug Console (window.dbg) ===

FLAGS:
  dbg.flags()                  Show all flag states
  dbg.flag('playerGodMode')    Toggle a flag
  dbg.master()                 Toggle master debug
  dbg.master(true/false)       Set master debug

PLAYER:
  dbg.hp(50)                   Set player HP
  dbg.heal()                   Heal to full
  dbg.kill()                   Kill the player

ENEMIES:
  dbg.spawn('grunt', 3)        Spawn 3 grunts near player
  dbg.despawnAll()             Remove all enemies

XP:
  dbg.xp('grit', 500)         Grant 500 XP to grit tree
  dbg.xp('all', 2000)         Grant 2000 XP to all trees

SLOTS:
  dbg.maxSlots()               Max out attunement slots
`, 'color: #aaffaa');
    },

    flag(name: string) {
      const allFlags = DebugConfig.allFlags;
      if (!allFlags.includes(name as DebugFlagKey)) {
        console.warn(`dbg: Unknown flag '${name}'. Available: ${allFlags.join(', ')}`);
        return;
      }
      DebugConfig.toggle(name as DebugFlagKey);
      const on = DebugConfig.rawFlags[name as DebugFlagKey];
      log(`${name} = ${on ? 'ON' : 'OFF'}`);
    },

    flags() {
      const allFlags = DebugConfig.allFlags;
      const masterOn = DebugConfig.enabled;
      console.log(`%cMaster: ${masterOn ? 'ON' : 'OFF'}`, `color: ${masterOn ? '#44ff44' : '#ff4444'}`);
      for (const flag of allFlags) {
        const raw = DebugConfig.rawFlags[flag];
        const effective = DebugConfig.flags[flag];
        const color = effective ? '#44ff44' : raw ? '#888844' : '#ff4444';
        console.log(`%c  ${flag}: ${raw ? 'ON' : 'OFF'}${raw && !effective ? ' (master off)' : ''}`, `color: ${color}`);
      }
    },

    master(on?: boolean) {
      if (on === undefined) {
        DebugConfig.setEnabled(!DebugConfig.enabled);
      } else {
        DebugConfig.setEnabled(on);
      }
      log(`Master debug: ${DebugConfig.enabled ? 'ON' : 'OFF'}`);
    },

    hp(value: number) {
      const gs = getGameScene();
      if (!gs?.player?.health) { console.warn('dbg: No player'); return; }
      gs.player.health.setHp(value);
      log(`Player HP set to ${gs.player.health.hp}/${gs.player.health.max}`);
    },

    heal() {
      const gs = getGameScene();
      if (!gs?.player?.health) { console.warn('dbg: No player'); return; }
      gs.player.health.setHp(gs.player.health.max);
      log(`Player healed to ${gs.player.health.max}`);
    },

    kill() {
      const gs = getGameScene();
      if (!gs?.player?.health) { console.warn('dbg: No player'); return; }
      gs.player.health.setHp(0);
      log('Player killed');
    },

    spawn(archetype: string, count: number = 1) {
      const gs = getGameScene();
      if (!gs?.enemyManager || !gs?.player) { console.warn('dbg: No game scene'); return; }
      const px = gs.player.sprite.x;
      const py = gs.player.sprite.y;
      let spawned = 0;
      for (let i = 0; i < count; i++) {
        const angle = randomAngle();
        const dist = randomInRange(150, 300);
        const dir = angleToDirection(angle);
        const e = gs.enemyManager.spawnAt(px + dir.x * dist, py + dir.y * dist, archetype);
        if (e) spawned++;
      }
      log(`Spawned ${spawned}x ${archetype}`);
    },

    despawnAll() {
      const gs = getGameScene();
      if (!gs?.enemyManager) { console.warn('dbg: No game scene'); return; }
      const all = [...gs.enemyManager.all];
      for (const enemy of all) {
        gs.enemyManager.removeEnemy(enemy);
      }
      log(`Despawned ${all.length} enemies`);
    },

    xp(tree: string, amount: number) {
      const gs = getGameScene();
      if (!gs?.player?.skillProgression) { console.warn('dbg: No player'); return; }
      const prog = gs.player.skillProgression;

      if (tree === 'all') {
        for (const treeName of Object.keys(ConfigLoader.v4Trees)) {
          prog.grantXp(treeName, amount);
        }
        log(`Granted ${amount} XP to all trees`);
      } else {
        if (!(tree in ConfigLoader.v4Trees)) {
          console.warn(`dbg: Unknown tree '${tree}'. Available: ${Object.keys(ConfigLoader.v4Trees).join(', ')}`);
          return;
        }
        prog.grantXp(tree, amount);
        log(`Granted ${amount} XP to ${tree} (now ${prog.getTreeXp(tree)})`);
      }
    },

    maxSlots() {
      log('V4 uses fixed 4 hotbar slots — no slot growth needed');
    },
  };

  window.dbg = api;
  console.log('%c[dbg] Debug console ready. Type dbg.help() for commands.', 'color: #44ff44');
}
